// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import notify from '../utils/toast';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import ActionButtons from '../components/ActionButtons';
import AppModal from '../components/AppModal';

const STATUS_BADGE = {
    available: 'success',
    booked: 'warning',
    sold: 'primary',
    transferred: 'secondary',
    reserved: 'info',
};

const BUILDING_PRESETS = [
    { label: 'G+5', value: 'G+5' },
    { label: 'G+10', value: 'G+10' },
    { label: 'BG+10', value: 'BG+10' },
    { label: 'B+G+12', value: 'B+G+12' },
    { label: 'B1+B2+G+15', value: 'B1+B2+G+15' },
    { label: 'G+20', value: 'G+20' },
    { label: '1–12 (typical only)', value: '1-12' },
];

export default function Units() {
    const [units, setUnits] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const [showModal, setShowModal] = useState(false);
    const [showBulk, setShowBulk] = useState(false);
    const [editId, setEditId] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    const [form, setForm] = useState({
        project_id: '',
        unit_number: '',
        floor: '',
        block: '',
        unit_type: 'flat',
        size_sqft: '',
        facing: '',
        sale_price: '',
        status: 'available',
        bedrooms: '',
        bathrooms: '',
        balcony: '',
        land_share_decimal: '',
        plot_no: '',
    });
    const [bulk, setBulk] = useState({
        project_id: '',
        building: 'G+10',
        blocks: 'A',
        units_per_floor: 4,
        numbering: 'floor_unit',
        unit_type: 'flat',
        sale_price: '',
        size_sqft: '',
        bedrooms: '3',
        bathrooms: '2',
        facing: '',
        prefix: '',
        skip_existing: true,
        parking_on_bg: true,
        parking_units_per_floor: 2,
        parking_price: '',
    });
    const { can } = useAuth();
    const { t, td} = useI18n();

    const projectId = searchParams.get('project_id') || '';
    const status = searchParams.get('status') || '';

    const load = () => {
        setLoading(true);
        const params = {};
        if (projectId) params.project_id = projectId;
        if (status) params.status = status;
        Promise.all([api.get('/units', { params }), api.get('/projects')])
            .then(([u, p]) => {
                setUnits(u.data.data);
                setProjects(p.data.data);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, [projectId, status]);

    const emptyForm = () => ({
        project_id: projectId || '',
        unit_number: '',
        floor: '',
        block: '',
        unit_type: 'flat',
        size_sqft: '',
        facing: '',
        sale_price: '',
        status: 'available',
        bedrooms: '',
        bathrooms: '',
        balcony: '',
        land_share_decimal: '',
        plot_no: '',
    });

    const openCreate = () => {
        setEditId(null);
        setForm(emptyForm());
        setShowModal(true);
    };

    const closeUnitModal = () => {
        setShowModal(false);
        setEditId(null);
        setForm(emptyForm());
    };

    const emptyBulk = () => ({
        project_id: projectId || '',
        building: 'G+10',
        blocks: 'A',
        units_per_floor: 4,
        numbering: 'floor_unit',
        unit_type: 'flat',
        sale_price: '',
        size_sqft: '',
        bedrooms: '3',
        bathrooms: '2',
        facing: '',
        prefix: '',
        skip_existing: true,
        parking_on_bg: true,
        parking_units_per_floor: 2,
        parking_price: '',
    });

    const closeBulkModal = () => {
        setShowBulk(false);
        setPreview(null);
        setBulk(emptyBulk());
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...form,
                sale_price: Number(form.sale_price),
                size_sqft: form.size_sqft ? Number(form.size_sqft) : null,
                bedrooms: form.bedrooms ? Number(form.bedrooms) : null,
                bathrooms: form.bathrooms ? Number(form.bathrooms) : null,
                balcony: form.balcony ? Number(form.balcony) : null,
                land_share_decimal: form.land_share_decimal ? Number(form.land_share_decimal) : null,
            };
            if (editId) {
                await api.put(`/units/${editId}`, payload);
            } else {
                await api.post('/units', payload);
            }
            closeUnitModal();
            load();
        } catch (err) {
            notify.apiError(err);
        }
    };

    const runPreview = async () => {
        try {
            const res = await api.post('/units/bulk/preview', { generate: bulk });
            setPreview(res.data.data);
        } catch (err) {
            notify.apiError(err);
        }
    };

    const handleBulk = async (e) => {
        e.preventDefault();
        if (!bulk.project_id) {
            notify.info('Select a project');
            return;
        }
        setBulkBusy(true);
        try {
            const res = await api.post('/units/bulk', {
                generate: {
                    ...bulk,
                    sale_price: bulk.sale_price ? Number(bulk.sale_price) : 0,
                    size_sqft: bulk.size_sqft ? Number(bulk.size_sqft) : null,
                    bedrooms: bulk.bedrooms !== '' ? Number(bulk.bedrooms) : null,
                    bathrooms: bulk.bathrooms !== '' ? Number(bulk.bathrooms) : null,
                    parking_on_bg: !!bulk.parking_on_bg,
                    parking_units_per_floor: Number(bulk.parking_units_per_floor) || 2,
                    parking_price: bulk.parking_price !== '' ? Number(bulk.parking_price) : 0,
                },
            });
            const n = res.data.meta?.created || res.data.data?.length || 0;
            notify.success(t('units_created_n', { count: n }));
            closeBulkModal();
            load();
        } catch (err) {
            notify.apiError(err);
        } finally {
            setBulkBusy(false);
        }
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div>
                    <h4 className="mb-1">{t('inventory')}</h4>
                    <p className="text-muted mb-0">{t('inventoryDesc')}</p>
                </div>
                <div className="d-flex flex-wrap gap-2">
                    <select
                        className="form-select form-select-sm"
                        style={{ width: 180 }}
                        value={projectId}
                        onChange={(e) =>
                            setSearchParams({
                                ...(status && { status }),
                                ...(e.target.value && { project_id: e.target.value }),
                            })
                        }
                    >
                        <option value="">All Projects</option>
                        {projects.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.name}
                            </option>
                        ))}
                    </select>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: 140 }}
                        value={status}
                        onChange={(e) =>
                            setSearchParams({
                                ...(projectId && { project_id: projectId }),
                                ...(e.target.value && { status: e.target.value }),
                            })
                        }
                    >
                        <option value="">All Status</option>
                        <option value="available">Available</option>
                        <option value="booked">Booked</option>
                        <option value="sold">Sold</option>
                    </select>
                    {can('units.create') && (
                        <>
                            <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => {
                                    setBulk((b) => ({ ...b, project_id: projectId || b.project_id }));
                                    setShowBulk(true);
                                    setPreview(null);
                                }}
                            >
                                <i className="bi bi-grid-3x3-gap me-1" />
                                {t('bulkAdd')}
                            </button>
                            <button className="btn btn-primary btn-sm" onClick={openCreate}>
                                <i className="bi bi-plus-lg me-1" />
                                {t('addUnit')}
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="card table-card">
                <div className="table-responsive">
                    <table className="table table-hover mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Unit</th>
                                <th>Project</th>
                                <th>Type</th>
                                <th>Floor/Block</th>
                                <th>Size / Specs</th>
                                <th>Price</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="text-center py-4">
                                        <div className="spinner-border spinner-border-sm text-success" />
                                    </td>
                                </tr>
                            ) : units.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="text-center text-muted py-4">
                                        {t('noUnitsHint')}
                                    </td>
                                </tr>
                            ) : (
                                units.map((u) => (
                                    <tr key={u.id}>
                                        <td className="fw-semibold">{u.unit_number}</td>
                                        <td>{u.project?.name}</td>
                                        <td className="text-capitalize">{td('unit_type', u.unit_type)}</td>
                                        <td>
                                            {u.floor || '—'} / {u.block || '—'}
                                        </td>
                                        <td>
                                            {u.size_sqft ? `${u.size_sqft} sqft` : '—'}
                                            {(u.unit_type === 'flat' || u.unit_type === 'apartment') && u.bedrooms
                                                ? ` · ${u.bedrooms} bed`
                                                : ''}
                                            {u.unit_type === 'land_share' && u.land_share_decimal
                                                ? ` · share ${u.land_share_decimal}`
                                                : ''}
                                        </td>
                                        <td>৳ {Number(u.sale_price || 0).toLocaleString()}</td>
                                        <td>
                                            <span className={`badge bg-${STATUS_BADGE[u.status] || 'secondary'}`}>
                                                {td('status', u.status)}
                                            </span>
                                        </td>
                                        <td>
                                            <ActionButtons
                                                canView={can('units.view')}
                                                canEdit={can('units.edit')}
                                                canDelete={can('units.delete')}
                                                onEdit={() => {
                                                    setForm({
                                                        project_id: u.project_id || '',
                                                        unit_number: u.unit_number || '',
                                                        unit_type: u.unit_type || 'flat',
                                                        floor: u.floor || '',
                                                        block: u.block || '',
                                                        size_sqft: u.size_sqft || '',
                                                        bedrooms: u.bedrooms || '',
                                                        bathrooms: u.bathrooms || '',
                                                        balcony: u.balcony || '',
                                                        sale_price: u.sale_price || '',
                                                        status: u.status || 'available',
                                                        land_share_decimal: u.land_share_decimal || '',
                                                        plot_no: u.plot_no || '',
                                                        facing: u.facing || '',
                                                    });
                                                    setEditId(u.id);
                                                    setShowModal(true);
                                                }}
                                                onDelete={async () => {
                                                    if (!window.confirm('Delete this unit?')) return;
                                                    try {
                                                        await api.delete(`/units/${u.id}`);
                                                        load();
                                                    } catch (e) {
                                                        notify.apiError(e);
                                                    }
                                                }}
                                            />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Single unit modal */}
            {showModal && (
                <div className="modal show d-block app-modal" style={{ background: 'rgba(0,0,0,.5)' }} onClick={closeUnitModal}>
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
                        <form className="modal-content" onSubmit={handleCreate}>
                            <div className="modal-header">
                                <h5 className="modal-title">{editId ? t('editUnit') : t('addUnit')}</h5>
                                <button type="button" className="btn-close" onClick={closeUnitModal} />
                            </div>
                            <div className="modal-body">
                                <div className="row g-2">
                                    <div className="col-md-6">
                                        <label className="form-label">Project</label>
                                        <select
                                            className="form-select"
                                            required
                                            value={form.project_id}
                                            onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                                        >
                                            <option value="">Select…</option>
                                            {projects.map((p) => (
                                                <option key={p.id} value={p.id}>
                                                    {p.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Unit number</label>
                                        <input
                                            className="form-control"
                                            required
                                            value={form.unit_number}
                                            onChange={(e) => setForm({ ...form, unit_number: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Type</label>
                                        <select
                                            className="form-select"
                                            value={form.unit_type}
                                            onChange={(e) => setForm({ ...form, unit_type: e.target.value })}
                                        >
                                            <option value="flat">Flat</option>
                                            <option value="apartment">Apartment</option>
                                            <option value="land_share">Land share</option>
                                            <option value="parking">Parking</option>
                                            <option value="shop">Shop</option>
                                            <option value="commercial">Commercial</option>
                                            <option value="office">Office</option>
                                        </select>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Floor</label>
                                        <input
                                            className="form-control"
                                            value={form.floor}
                                            onChange={(e) => setForm({ ...form, floor: e.target.value })}
                                            placeholder="G, B, 1, 2…"
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Block</label>
                                        <input
                                            className="form-control"
                                            value={form.block}
                                            onChange={(e) => setForm({ ...form, block: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Size (sqft)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={form.size_sqft}
                                            onChange={(e) => setForm({ ...form, size_sqft: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Sale price</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            required
                                            value={form.sale_price}
                                            onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Status</label>
                                        <select
                                            className="form-select"
                                            value={form.status}
                                            onChange={(e) => setForm({ ...form, status: e.target.value })}
                                        >
                                            {Object.keys(STATUS_BADGE).map((s) => (
                                                <option key={s} value={s}>
                                                    {s}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {(form.unit_type === 'flat' || form.unit_type === 'apartment') && (
                                        <>
                                            <div className="col-md-4">
                                                <label className="form-label">Bedrooms</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={form.bedrooms}
                                                    onChange={(e) => setForm({ ...form, bedrooms: e.target.value })}
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">Bathrooms</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={form.bathrooms}
                                                    onChange={(e) => setForm({ ...form, bathrooms: e.target.value })}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeUnitModal}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Bulk generate modal */}
            {showBulk && (
                <div className="modal show d-block app-modal" style={{ background: 'rgba(0,0,0,.5)' }} onClick={closeBulkModal}>
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
                        <form className="modal-content" onSubmit={handleBulk}>
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="bi bi-building me-2" />
                                    {t('bulkUnitsTitle')}
                                </h5>
                                <button type="button" className="btn-close" onClick={closeBulkModal} />
                            </div>
                            <div className="modal-body">
                                <p className="small text-muted">{t('bulkUnitsHelp')}</p>
                                <p className="small text-muted mb-2">{t('parkingOnBgHelp')}</p>
                                <div className="row g-2">
                                    <div className="col-md-6">
                                        <label className="form-label">{t('project')} *</label>
                                        <select
                                            className="form-select"
                                            required
                                            value={bulk.project_id}
                                            onChange={(e) => setBulk({ ...bulk, project_id: e.target.value })}
                                        >
                                            <option value="">Select…</option>
                                            {projects.map((p) => (
                                                <option key={p.id} value={p.id}>
                                                    {p.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">{t('buildingExpression')} *</label>
                                        <div className="input-group">
                                            <input
                                                className="form-control"
                                                required
                                                value={bulk.building}
                                                onChange={(e) => setBulk({ ...bulk, building: e.target.value })}
                                                placeholder="G+10"
                                            />
                                            <select
                                                className="form-select"
                                                style={{ maxWidth: 140 }}
                                                value=""
                                                onChange={(e) => {
                                                    if (e.target.value) setBulk({ ...bulk, building: e.target.value });
                                                }}
                                            >
                                                <option value="">{t('preset')}</option>
                                                {BUILDING_PRESETS.map((p) => (
                                                    <option key={p.value} value={p.value}>
                                                        {p.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">{t('blocks')}</label>
                                        <input
                                            className="form-control"
                                            value={bulk.blocks}
                                            onChange={(e) => setBulk({ ...bulk, blocks: e.target.value })}
                                            placeholder="A or A,B,C"
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">{t('unitsPerFloor')}</label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={20}
                                            className="form-control"
                                            value={bulk.units_per_floor}
                                            onChange={(e) => setBulk({ ...bulk, units_per_floor: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">{t('numbering')}</label>
                                        <select
                                            className="form-select"
                                            value={bulk.numbering}
                                            onChange={(e) => setBulk({ ...bulk, numbering: e.target.value })}
                                        >
                                            <option value="floor_unit">301, 302 / G01 (floor+unit)</option>
                                            <option value="block_floor_unit">A-301 (block-floor-unit)</option>
                                            <option value="block_floor_letter">A-3A, A-3B (letter)</option>
                                            <option value="sequential">A-3-1 sequential</option>
                                        </select>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">{t('unitType')}</label>
                                        <select
                                            className="form-select"
                                            value={bulk.unit_type}
                                            onChange={(e) => setBulk({ ...bulk, unit_type: e.target.value })}
                                        >
                                            <option value="flat">Flat</option>
                                            <option value="apartment">Apartment</option>
                                            <option value="shop">Shop</option>
                                            <option value="office">Office</option>
                                            <option value="parking">Parking</option>
                                        </select>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">{t('defaultPrice')}</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={bulk.sale_price}
                                            onChange={(e) => setBulk({ ...bulk, sale_price: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">{t('sizeSqft')}</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={bulk.size_sqft}
                                            onChange={(e) => setBulk({ ...bulk, size_sqft: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">{t('bedrooms')}</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={bulk.bedrooms}
                                            onChange={(e) => setBulk({ ...bulk, bedrooms: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">{t('bathrooms')}</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={bulk.bathrooms}
                                            onChange={(e) => setBulk({ ...bulk, bathrooms: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">{t('prefix')}</label>
                                        <input
                                            className="form-control"
                                            value={bulk.prefix}
                                            onChange={(e) => setBulk({ ...bulk, prefix: e.target.value })}
                                            placeholder={t('optional')}
                                        />
                                    </div>
                                    <div className="col-md-3 d-flex align-items-end">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="skipEx"
                                                checked={bulk.skip_existing}
                                                onChange={(e) => setBulk({ ...bulk, skip_existing: e.target.checked })}
                                            />
                                            <label className="form-check-label small" htmlFor="skipEx">
                                                {t('skipExisting')}
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                
                                    <div className="col-md-6">
                                        <div className="form-check mt-4">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="parkingOnBg"
                                                checked={bulk.parking_on_bg}
                                                onChange={(e) => setBulk({ ...bulk, parking_on_bg: e.target.checked })}
                                            />
                                            <label className="form-check-label small" htmlFor="parkingOnBg">
                                                {t('parkingOnBg')}
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">{t('parkingUnitsPerFloor')}</label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={40}
                                            className="form-control"
                                            value={bulk.parking_units_per_floor}
                                            onChange={(e) => setBulk({ ...bulk, parking_units_per_floor: e.target.value })}
                                            disabled={!bulk.parking_on_bg}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">{t('parkingPrice')}</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={bulk.parking_price}
                                            onChange={(e) => setBulk({ ...bulk, parking_price: e.target.value })}
                                            disabled={!bulk.parking_on_bg}
                                        />
                                    </div>

                                {preview && (
                                    <div className="alert alert-info mt-3 mb-0 small">
                                        <strong>{t('previewLabel')}:</strong>{' '}
                                        {t('floorsLabel')} [{preview.floors?.join(', ')}]
                                        {preview.residential_floors?.length != null && (
                                            <> · {t('residentialUnits')} [{preview.residential_floors?.join(', ') || '—'}]</>
                                        )}
                                        {preview.parking_floors?.length > 0 && (
                                            <> · {t('parkingUnits')} [{preview.parking_floors?.join(', ')}]</>
                                        )}
                                        {' '}· {t('blocksLabel')} [{preview.blocks?.join(', ') || '—'}]
                                        {' '}· {preview.units_per_floor}{t('perFloor')}
                                        {' '}→ <strong>{t('residentialUnits')}: {preview.estimated_residential ?? '—'}</strong>
                                        {preview.estimated_parking != null && (
                                            <> · <strong>{t('parkingUnits')}: {preview.estimated_parking}</strong></>
                                        )}
                                        {' '}· {t('totalUnitsLabel')}: <strong>{preview.estimated_units}</strong> {t('unitsWord')}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary" onClick={runPreview}>
                                    Preview floors
                                </button>
                                <button type="button" className="btn btn-secondary" onClick={closeBulkModal}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={bulkBusy}>
                                    {bulkBusy ? t('creating') : t('generateUnits')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
